import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getUsers, deleteUser } from '../services/adminApi';

function UserRow({ user, onDelete }) {
  const [deleting, setDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteUser(user.id);
      onDelete(user.id);
    } catch (err) {
      alert('Failed to delete user: ' + err.message);
    } finally {
      setDeleting(false);
      setShowConfirm(false);
    }
  };

  const statusColor = user.isBlocked
    ? 'coral'
    : user.subscriptionActive
    ? 'teal'
    : 'navy';

  return (
    <tr className="table-row border-b border-navy-700/50">
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-navy-600 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-medium text-white">
              {(user.name?.[0] || user.email?.[0] || 'U').toUpperCase()}
            </span>
          </div>
          <div className="min-w-0">
            <Link to={`/users/${user.id}`} className="font-medium text-white hover:text-teal-400 transition-colors">
              {user.name || 'Unnamed User'}
            </Link>
            <p className="text-sm text-navy-400 truncate">{user.email}</p>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 hidden md:table-cell">
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-${statusColor}-500/20 text-${statusColor}-400`}>
          {user.isBlocked ? 'Blocked' : user.subscriptionActive ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td className="px-6 py-4 hidden lg:table-cell">
        <span className="text-navy-300 text-sm">{user.subscriptionType || 'None'}</span>
      </td>
      <td className="px-6 py-4 hidden xl:table-cell">
        <span className="text-navy-400 text-sm font-mono">{user.documentsCount || 0}</span>
      </td>
      <td className="px-6 py-4 hidden xl:table-cell">
        <span className="text-navy-400 text-sm">
          {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
        </span>
      </td>
      <td className="px-6 py-4 text-right">
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/users/${user.id}`}
            className="p-2 text-navy-400 hover:text-white hover:bg-navy-700 rounded-lg transition-colors"
            title="View details"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </Link>
          {showConfirm ? (
            <div className="flex items-center gap-1">
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="p-2 text-coral-400 hover:text-coral-300 hover:bg-coral-600/20 rounded-lg transition-colors"
                title="Confirm delete"
              >
                {deleting ? (
                  <div className="w-4 h-4 border-2 border-coral-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                className="p-2 text-navy-400 hover:text-white hover:bg-navy-700 rounded-lg transition-colors"
                title="Cancel"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowConfirm(true)}
              className="p-2 text-navy-400 hover:text-coral-400 hover:bg-coral-600/10 rounded-lg transition-colors"
              title="Delete user"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function UserList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [nextToken, setNextToken] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadUsers = useCallback(async (append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const params = { limit: 25 };
      if (search) params.search = search;
      if (status !== 'all') params.status = status;
      if (append && nextToken) params.nextToken = nextToken;

      const data = await getUsers(params);
      
      if (append) {
        setUsers(prev => [...prev, ...data.users]);
      } else {
        setUsers(data.users);
      }
      setNextToken(data.nextToken);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [search, status, nextToken]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      loadUsers();
    }, 300);
    return () => clearTimeout(debounce);
  }, [search, status]);

  const handleDelete = (userId) => {
    setUsers(prev => prev.filter(u => u.id !== userId));
  };

  const exportCsv = () => {
    const headers = ['ID', 'Name', 'Email', 'Status', 'Subscription', 'Documents', 'Created'];
    const rows = users.map(u => [
      u.id,
      u.name || '',
      u.email || '',
      u.isBlocked ? 'Blocked' : u.subscriptionActive ? 'Active' : 'Inactive',
      u.subscriptionType || '',
      u.documentsCount || 0,
      u.createdAt || '',
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `crewbook-users-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Users</h1>
          <p className="text-navy-300 mt-1">Manage CrewBook user accounts</p>
        </div>
        <button onClick={exportCsv} className="btn-secondary flex items-center gap-2 self-start">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="input-field pl-10"
              />
            </div>
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="input-field w-full sm:w-48"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="blocked">Blocked</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden p-0">
        {loading ? (
          <div className="p-8 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full loading-shimmer" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/3 loading-shimmer rounded" />
                  <div className="h-3 w-1/4 loading-shimmer rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <p className="text-coral-400">{error}</p>
            <button onClick={() => loadUsers()} className="btn-primary mt-4">
              Retry
            </button>
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-navy-700 flex items-center justify-center">
              <svg className="w-8 h-8 text-navy-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <p className="text-navy-300">No users found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-navy-800/50 border-b border-navy-700">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-navy-300 uppercase tracking-wider">User</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-navy-300 uppercase tracking-wider hidden md:table-cell">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-navy-300 uppercase tracking-wider hidden lg:table-cell">Subscription</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-navy-300 uppercase tracking-wider hidden xl:table-cell">Docs</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-navy-300 uppercase tracking-wider hidden xl:table-cell">Created</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-navy-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <UserRow key={user.id} user={user} onDelete={handleDelete} />
                  ))}
                </tbody>
              </table>
            </div>
            {nextToken && (
              <div className="p-4 border-t border-navy-700 text-center">
                <button
                  onClick={() => loadUsers(true)}
                  disabled={loadingMore}
                  className="btn-secondary"
                >
                  {loadingMore ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Loading...
                    </div>
                  ) : (
                    'Load More'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
